/**
 * Calendar layout constants used across all rental calendar components
 */
export const CALENDAR_CONSTANTS = {
  ROW_HEIGHT: 57,
  HEADER_HEIGHT: 28,
  TITLE_COLUMN_WIDTH: 200,
  COLUMN_WIDTH: 72,
  /** Draw a stronger separator every N hours in day view */
  MAJOR_HOUR_STEP: 6,
  /** Shorter than a vehicle row — a group separator carries a label, not a timeline */
  GROUP_ROW_HEIGHT: 32,
} as const;

export type CalendarConstants = typeof CALENDAR_CONSTANTS;

/**
 * Grid lines are drawn as inset box-shadows, not borders.
 *
 * The platform's `resetCss` sets `border-width: 0` on `*` from an unlayered stylesheet, which beats
 * Tailwind's `@layer utilities` — every `border-*` class in DMS renders nothing (same workaround as
 * `libs/features/eag-grid/src/constants/borderStyles.ts`). A shadow also costs no layout, so the
 * 72px column width keeps matching the day columns the slot selection resolves against.
 */
const SEPARATOR_LINE = 'var(--color-separator)';
const ROW_LINE = 'var(--color-palettes-neutral-20-100)';
const MAJOR_LINE = 'var(--color-palettes-neutral-50-100)';

export const CALENDAR_LINES = {
  columnRight: {boxShadow: `inset -1px 0 0 ${SEPARATOR_LINE}`},
  columnRightMajor: {boxShadow: `inset -1px 0 0 ${MAJOR_LINE}`},
  rowBottom: {boxShadow: `inset 0 -1px 0 ${ROW_LINE}`},
  titleCell: {boxShadow: `inset -1px 0 0 ${SEPARATOR_LINE}, inset 0 -1px 0 ${ROW_LINE}`},
  headerBottom: {boxShadow: `inset 0 -1px 0 ${SEPARATOR_LINE}`},
  headerCorner: {boxShadow: `inset -1px 0 0 ${SEPARATOR_LINE}, inset 0 -1px 0 ${SEPARATOR_LINE}`},
  /** Whole-column tint while its day header is hovered */
  hoveredColumn: {backgroundColor: ROW_LINE},
  /** Cells covered by an in-progress drag selection */
  selectedCell: {backgroundColor: 'var(--color-palettes-blue-20-100)'},
} as const;
